import React from "react";
import FloatingCard from "../../../ui/FloatingCard";
import { eventPoints } from "../utils/liveScoreState";

// ── Advanced-score sentence templates ────────────────────────────────────────
// Each entry: [icon, positiveTemplate, scoresSuffix]
// Use {player} as the placeholder for the player's first name / display name.
const METRIC_SENTENCES = {
  // Volleyball
  kill:            ['🏐', '{player} nailed a kill!',             true],
  attackError:     ['❌', '{player} made an attack error',        false],
  attackAttempt:   ['🏐', '{player} attempted an attack',        false],
  ace:             ['⚡', '{player} served an ace!',              true],
  serviceAce:      ['⚡', '{player} served an ace!',              true],
  serviceError:    ['❌', '{player} made a service error',        false],
  blockSolo:       ['🛡️', '{player} solo-blocked the attack!',   true],
  blockAssist:     ['🛡️', '{player} assisted the block!',        true],
  dig:             ['🤜', '{player} dug it out!',                 false],
  assist:          ['🎯', '{player} set up the play',             false],
  // Basketball
  points:          ['🏀', '{player} scored!',                     true],
  steal:           ['✋', '{player} stole the ball!',             false],
  blockShot:       ['🛡️', '{player} blocked the shot!',          false],
  offensiveRebound:['🔃', '{player} grabbed an offensive rebound',false],
  defensiveRebound:['🔃', '{player} grabbed a defensive rebound', false],
  turnover:        ['❌', '{player} turned the ball over',        false],
  // Badminton / Tennis
  smash:           ['💥', '{player} landed a smash!',             true],
  dropShot:        ['🏸', '{player} played a drop shot',          false],
  clear:           ['🏸', '{player} sent a clear',                false],
  netShot:         ['🏸', '{player} played a net shot',           false],
  volley:          ['🎾', '{player} won at the net!',             true],
  winner:          ['🎾', '{player} hit a winner!',               true],
  otherWinner:     ['🏸', '{player} hit a winner!',               true],
  doubleFault:     ['❌', '{player} double-faulted',              false],
  serviceFault:    ['❌', '{player} made a service fault',        false],
  unforcedError:   ['❌', '{player} made an unforced error',      false],
  forcedError:     ['❌', '{player} was forced into an error',    false],
  rotationError:   ['❌', '{player} had a rotation error',        false],
  positioningError:['❌', '{player} had a positioning error',     false],
  // Football
  goal:            ['⚽', '{player} scored a goal!',              true],
  ownGoal:         ['⚽', '{player} scored an own goal',           false],
  shotOnTarget:    ['🎯', '{player} hit the target',              false],
  save:            ['🧤', '{player} made a save!',                false],
  tackle:          ['💪', '{player} won the tackle!',             false],
  interception:    ['✋', '{player} intercepted the ball!',       false],
  passAccuracy:    ['🎯', '{player} — pass accuracy logged',      false],
  // Discipline (shared)
  foul:            ['⚠️', '{player} committed a foul',           false],
  yellowCard:      ['🟨', '{player} received a yellow card',      false],
  redCard:         ['🟥', '{player} was sent off (red card)',      false],
  communication:   ['👥', '{player} — communication noted',       false],
};

/** "+2", "+3" … — the points the event really put on the board (not always +1). */
export const pointsSuffix = (event) => {
  const pts = eventPoints(event);
  return pts > 0 ? ` +${pts}` : '';
};

export const buildAdvancedSentence = (event) => {
  const firstName = (event.playerName || 'Player').split(' ')[0];
  const key = event.metricKey;
  const entry = METRIC_SENTENCES[key];

  let icon, sentence;
  if (entry) {
    const [_icon, template] = entry;
    icon = _icon;
    sentence = template.replace('{player}', firstName);
    if (event.typeLabel) sentence += ` · ${event.typeLabel}`;
  } else {
    // Fallback for any unrecognised metric
    icon = event.isPositive ? '📋' : '❌';
    sentence = `${firstName} — ${event.metricLabel || key}`;
  }
  if (event.affectsScore) sentence += pointsSuffix(event);
  return { icon, sentence };
};

/** Signed points of a plain score event ("+3", "−1"), or null. */
export const scoreDeltaLabel = (event) => {
  const pts = Number(event?.points);
  if (!Number.isFinite(pts) || pts === 0) return null;
  return pts > 0 ? `+${pts}` : `−${Math.abs(pts)}`;
};

const LiveEventFeed = ({ eventLog, isMatchActive, connected, match }) => {
  return (
    <FloatingCard
      variant="outline"
      size="medium"
      className="live-events-websocket delay-5"
    >
      <div className="events-header">
        <h3>📊 Live Event Feed</h3>
        {isMatchActive && connected && (
          <div className="live-indicator">
            <span className="pulse-dot"></span>
            Real-time Updates
          </div>
        )}
      </div>

      <div className="events-content">
        {eventLog.length > 0 ? (
          <div className="events-list-websocket">
            {eventLog.filter((event) => event && (event.type || event.id)).map((event, index) => {
              // ── session-restore divider ──
              if (event.type === "session_restore") {
                return (
                  <div key={`${event.id}-${index}`} className="event-session-divider">
                    <span>🔄 Session restored</span>
                  </div>
                );
              }

              // ── advanced score event ──
              if (event.type === "advanced_score") {
                const { icon, sentence } = buildAdvancedSentence(event);
                const teamName = event.participant && match
                  ? (event.participant === "participant1"
                    ? (match.participant_1_name || match.participant1Name || "Team 1")
                    : (match.participant_2_name || match.participant2Name || "Team 2"))
                  : null;
                return (
                  <div
                    key={`${event.id || "adv"}-${index}-${event.timestamp || Date.now()}`}
                    className={`event-item-websocket event-item-advanced ${event.affectsScore ? 'event-item-score' : ''} ${event.isPositive === false ? 'event-item-negative' : ''}`}
                  >
                    <div className="event-time">{event.timestamp || "N/A"}</div>
                    <div className="event-details">
                      <span className="event-type-badge">
                        {icon} {event.affectsScore ? 'SCORE' : 'STAT'}
                      </span>
                      {teamName && <span className="event-participant">{teamName}</span>}
                      <span className="event-description">{sentence}</span>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={`${event.id || "event"}-${index}-${event.timestamp || Date.now()}`}
                  className="event-item-websocket"
                >
                  <div className="event-time">{event.timestamp || "N/A"}</div>
                  <div className="event-details">
                    <span className="event-type-badge">
                      {event.type === "score_increase" ? "📈" :
                      event.type === "score_decrease" ? "📉" :
                      event.type === "score_reset" ? "🔄" :
                      event.type === "game_start" ? "🎬" :
                      event.type === "score_update" ? "⚽" :
                      event.type === "live_score_update" ? "⚽" :
                      event.type === "yellow_card" ? "🟨" :
                      event.type === "timeout" ? "⏸️" :
                      event.type === "timeout_end" ? "▶️" :
                      event.type === "match_ended" ? "🏆" :
                      event.type === "substitution" ? "🔄" : "📋"}
                      {(event.type || "unknown").replace(/_/g, " ").toUpperCase()}
                    </span>
                    {!event.hideParticipantName && (
                      <span className="event-participant">
                        {event.participantName ||
                          (event.participant && match
                            ? (event.participant === "participant1"
                              ? (match.participant_1_name || match.participant1Name || "Player 1")
                              : (match.participant_2_name || match.participant2Name || "Player 2"))
                            : "Unknown Player")}
                      </span>
                    )}
                    {scoreDeltaLabel(event) && (event.type === "score_increase" || event.type === "score_decrease") && (
                      <span className="event-points">{scoreDeltaLabel(event)}</span>
                    )}
                    {event.description && (
                      <span className="event-description">{event.description}</span>
                    )}
                    {event.score !== undefined && (
                      <span className="event-score">Score: {event.score}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-events">
            <div className="no-events-icon">📊</div>
            <p>No events yet</p>
            <p className="no-events-subtitle">
              {isMatchActive
                ? "Live events will appear here as they happen"
                : match?.status === "completed"
                  ? "This match finished without a point-by-point log"
                  : ["cancelled", "expired"].includes(match?.status)
                    ? "This match did not go ahead"
                    : "Start the match to see live updates"}
            </p>
          </div>
        )}
      </div>
    </FloatingCard>
  );
};

export default LiveEventFeed;
